import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Email as EmailIcon,
  Sms as SmsIcon,
  Send as SendIcon,
  Science as TestIcon,
  History as HistoryIcon,
  Payment as PaymentIcon,
  AccountBalance as BankIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSnackbar } from '../contexts/SnackbarContext';
import PaymentSettingsManager from '../components/PaymentSettingsManager';

interface NotificationTest {
  email?: string;
  phone?: string;
  message?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testData, setTestData] = useState<NotificationTest>({
    email: '',
    phone: '',
    message: 'Test notification from BSMMU Alumni Form Builder'
  });
  const [systemConfig, setSystemConfig] = useState({
    smsEnabled: true,
    emailEnabled: false,
    smsApiKey: '',
    smsApiEndpoint: '',
    smtpHost: '',
    smtpUser: '',
    smtpPassword: '',
    fromEmail: '',
    smtpPort: 587,
    // bKash settings
    bkashEnabled: true,
    bkashSandbox: true,
    bkashAppKey: '',
    bkashAppSecret: '',
    bkashUsername: '',
    bkashPassword: '',
    // Bank Transfer settings
    bankTransferEnabled: true,
    bankTransferInstructions: ''
  });
  const { showSnackbar } = useSnackbar();

  // Load settings on component mount
  useEffect(() => {
    loadSystemSettings();
  }, []);

  const loadSystemSettings = async () => {
    try {
      // Load system settings from API
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/system`);
      
      if (response.data.success && response.data.settings) {
        const settings = response.data.settings;
        
        // Convert settings object to systemConfig format
        const loadedConfig = {
          smsEnabled: settings.sms_enabled === 'true',
          emailEnabled: settings.email_enabled === 'true',
          smsApiKey: settings.sms_api_key || '',
          smsApiEndpoint: settings.sms_api_endpoint || '',
          smtpHost: settings.smtp_host || '',
          smtpUser: settings.smtp_user || '',
          smtpPassword: settings.smtp_password || '',
          fromEmail: settings.from_email || '',
          smtpPort: parseInt(settings.smtp_port) || 587,
          bkashEnabled: settings.bkash_enabled === 'true',
          bkashSandbox: settings.bkash_sandbox === 'true',
          bkashAppKey: settings.bkash_app_key || '',
          bkashAppSecret: settings.bkash_app_secret || '',
          bkashUsername: settings.bkash_username || '',
          bkashPassword: settings.bkash_password || '',
          bankTransferEnabled: settings.bank_transfer_enabled === 'true',
          bankTransferInstructions: settings.bank_transfer_instructions || ''
        };
        
        setSystemConfig(loadedConfig);
        console.log('✅ System settings loaded successfully');
      }
    } catch (error) {
      console.error('Failed to load system settings:', error);
      // Keep current state as defaults if loading fails
    }
  };

  const saveSystemSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      // Save system configuration to backend
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/settings/system-config`,
        { systemConfig },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        showSnackbar('Settings saved successfully!', 'success');
      } else {
        showSnackbar('Failed to save settings', 'error');
      }
    } catch (error: any) {
      console.error('Save settings error:', error);
      showSnackbar(error.response?.data?.message || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleTestNotification = async () => {
    if (!testData.email && !testData.phone) {
      showSnackbar('Please provide either email or phone number', 'error');
      return;
    }

    try {
      setTesting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/submissions/test-notification`,
        testData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        showSnackbar('Test notification sent successfully!', 'success');
        setTestDialogOpen(false);
      } else {
        showSnackbar('Test notification failed', 'error');
      }
    } catch (error: any) {
      console.error('Test notification error:', error);
      showSnackbar(error.response?.data?.message || 'Failed to send test notification', 'error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            System Settings
          </Typography>
          <Button
            variant="contained"
            startIcon={<TestIcon />}
            onClick={() => setTestDialogOpen(true)}
            color="primary"
          >
            Test Notifications
          </Button>
        </Box>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="system settings tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<SmsIcon />} label="SMS Settings" />
          <Tab icon={<EmailIcon />} label="Email Settings" />
          <Tab icon={<PaymentIcon />} label="bKash Settings" />
          <Tab icon={<BankIcon />} label="Bank Transfer" />
          <Tab icon={<HistoryIcon />} label="Logs" />
        </Tabs>

        {/* SMS Settings Tab */}
        <TabPanel value={activeTab} index={0}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SmsIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6">SMS Configuration</Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>SMS API Endpoint:</strong> https://bdbo.bvir.us/sms/index5.php
                  <br />
                  <strong>Phone Format:</strong> Bangladesh numbers (e.g., 01712345678)
                  <br />
                  <strong>Status:</strong> {systemConfig.smsEnabled ? 
                    <Chip label="Configured" color="success" size="small" /> : 
                    <Chip label="Not Configured" color="error" size="small" />
                  }
                </Typography>
              </Alert>

              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  label="SMS API Key"
                  value={systemConfig.smsApiKey || '01712345678'}
                  onChange={(e) => setSystemConfig(prev => ({ ...prev, smsApiKey: e.target.value }))}
                  placeholder="01712345678"
                  helperText="Enter your SMS API key"
                />

                <TextField
                  fullWidth
                  label="SMS API Endpoint"
                  value={systemConfig.smsApiEndpoint || 'https://bdbo.bvir.us/sms/index5.php'}
                  onChange={(e) => setSystemConfig(prev => ({ ...prev, smsApiEndpoint: e.target.value }))}
                  placeholder="https://bdbo.bvir.us/sms/index5.php"
                  helperText="SMS API endpoint URL (can be changed to any SMS provider)"
                />

                {/* <TextField
                  fullWidth
                  label="Default Sender Number"
                  value="01712345678"
                  disabled
                  helperText="Default sender number for SMS notifications"
                /> */}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" gutterBottom>
                  Available SMS Variables:
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                    <strong>System Variables:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {[
                      { variable: 'formTitle', desc: 'Form name' },
                      { variable: 'submissionId', desc: 'Submission ID' },
                      { variable: 'uniqueId', desc: 'Unique reference ID' },
                      { variable: 'submissionTimestamp', desc: 'Submission date/time' },
                      { variable: 'allFields', desc: 'All form data' }
                    ].map(({ variable, desc }) => (
                      <Chip 
                        key={variable}
                        label={`{{${variable}}}`}
                        size="small" 
                        variant="outlined"
                        title={desc}
                      />
                    ))}
                  </Box>
                  
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                    <strong>Payment Variables:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {[
                      { variable: 'paymentAmount', desc: 'Payment amount' },
                      { variable: 'paymentMethod', desc: 'Payment method used' },
                      { variable: 'bkashTrxId', desc: 'bKash transaction ID' },
                      { variable: 'bkashPaymentId', desc: 'bKash payment ID' },
                      { variable: 'paymentStatus', desc: 'Payment status' }
                    ].map(({ variable, desc }) => (
                      <Chip 
                        key={variable}
                        label={`{{${variable}}}`}
                        size="small" 
                        variant="outlined"
                        color="secondary"
                        title={desc}
                      />
                    ))}
                  </Box>

                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                    <strong>Example SMS Templates:</strong>
                  </Typography>
                  <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, border: '1px solid #ddd' }}>
                    <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', mb: 1 }}>
                      "Hi {'{{firstName}}'}, payment of ৳{'{{paymentAmount}}'} received via {'{{paymentMethod}}'}. bKash TrxID: {'{{bkashTrxId}}'}"
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                      "Form: {'{{formTitle}}'} | Ref: {'{{uniqueId}}'} | All data: {'{{allFields}}'}"
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    SMS Features:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="✅ Automatic phone number formatting"
                        secondary="Handles various input formats (01714694499, +8801714694499, etc.)"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="✅ Enhanced template variables"
                        secondary="Includes payment info, bKash details, and form data"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="✅ Error handling"
                        secondary="Graceful failure without breaking form submissions"
                      />
                    </ListItem>
                  </List>
                </Box>

                {/* Save Button for SMS Settings */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={saveSystemSettings}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save SMS Settings'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Email Settings Tab */}
        <TabPanel value={activeTab} index={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6">Email Configuration</Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Status:</strong> {(systemConfig.smtpHost && systemConfig.smtpUser && systemConfig.smtpPassword && systemConfig.fromEmail) ? 
                    <Chip label="Configured" color="success" size="small" /> : 
                    <Chip label="Not Configured" color="warning" size="small" />
                  }
                  <br />
                  Configure all SMTP settings below to enable email notifications.
                  <br />
                  <strong>Current Configuration:</strong>
                  <br />
                  • SMTP Host: {systemConfig.smtpHost ? '✓ Set' : '✗ Not set'}
                  <br />
                  • SMTP User: {systemConfig.smtpUser ? '✓ Set' : '✗ Not set'}
                  <br />
                  • SMTP Password: {systemConfig.smtpPassword ? '✓ Set' : '✗ Not set'}
                  <br />
                  • From Email: {systemConfig.fromEmail ? '✓ Set' : '✗ Not set'}
                </Typography>
              </Alert>

              <Box display="flex" flexDirection="column" gap={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={systemConfig.emailEnabled}
                      onChange={(e) => setSystemConfig(prev => ({ ...prev, emailEnabled: e.target.checked }))}
                    />
                  }
                  label="Enable Email Notifications"
                />

                <TextField
                  fullWidth
                  label="SMTP Host"
                  value={systemConfig.smtpHost}
                  onChange={(e) => setSystemConfig(prev => ({ ...prev, smtpHost: e.target.value }))}
                  placeholder="smtp.gmail.com"
                  helperText="SMTP server hostname"
                />

                <Box display="flex" gap={2}>
                  <TextField
                    label="SMTP Port"
                    type="number"
                    value={systemConfig.smtpPort}
                    onChange={(e) => setSystemConfig(prev => ({ ...prev, smtpPort: parseInt(e.target.value) }))}
                    sx={{ width: '200px' }}
                  />
                  <TextField
                    fullWidth
                    label="SMTP Username"
                    value={systemConfig.smtpUser}
                    onChange={(e) => setSystemConfig(prev => ({ ...prev, smtpUser: e.target.value }))}
                    placeholder="your-email@gmail.com"
                  />
                </Box>

                <TextField
                  fullWidth
                  label="SMTP Password"
                  value={systemConfig.smtpPassword || ''}
                  onChange={(e) => setSystemConfig(prev => ({ ...prev, smtpPassword: e.target.value }))}
                  type="password"
                  placeholder="Your SMTP password or app password"
                  helperText="For Gmail, use App Password instead of your regular password"
                />

                <TextField
                  fullWidth
                  label="From Email Address"
                  value={systemConfig.fromEmail || ''}
                  onChange={(e) => setSystemConfig(prev => ({ ...prev, fromEmail: e.target.value }))}
                  type="email"
                  placeholder="noreply@yourcompany.com"
                  helperText="Email address that will appear as sender"
                />

                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>SMTP Configuration:</strong>
                    <br />
                    All SMTP settings can be configured here and will be saved to the database.
                    <br />
                    <br />
                    <strong>Required Fields:</strong>
                    <br />
                    • SMTP_HOST (e.g., smtp.gmail.com)
                    <br />
                    • SMTP_PORT (e.g., 587 for TLS, 465 for SSL)
                    <br />
                    • SMTP_USER (your email username)
                    <br />
                    • SMTP_PASS (your email password or app password)
                    <br />
                    • FROM_EMAIL (sender email address)
                    <br />
                    <br />
                    <strong>For Gmail:</strong> Enable 2FA and use App Passwords instead of your regular password.
                    <br />
                    <strong>For Outlook:</strong> Use smtp-mail.outlook.com with port 587.
                  </Typography>
                </Alert>

                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Email Features:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="✅ HTML email templates"
                        secondary="Professional formatted emails with submission details"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="✅ Template variables"
                        secondary="Use form fields, payment info, and system variables"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="✅ Automatic submission details"
                        secondary="Includes submission ID, timestamp, and unique identifier"
                      />
                    </ListItem>
                  </List>
                </Box>

                {/* Save Button for Email Settings */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={saveSystemSettings}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Email Settings'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* bKash Settings Tab */}
        <TabPanel value={activeTab} index={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PaymentIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6">bKash Payment Configuration</Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={systemConfig.bkashEnabled}
                      onChange={(e) => setSystemConfig(prev => ({ ...prev, bkashEnabled: e.target.checked }))}
                    />
                  }
                  label="Enable bKash Payments"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={systemConfig.bkashSandbox}
                      onChange={(e) => setSystemConfig(prev => ({ ...prev, bkashSandbox: e.target.checked }))}
                    />
                  }
                  label="Use Sandbox Mode (for testing)"
                />

                <Alert severity={systemConfig.bkashSandbox ? "info" : "warning"} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Current Mode:</strong> {systemConfig.bkashSandbox ? 'Sandbox (Testing)' : 'Production (Live)'}
                    <br />
                    <strong>API Endpoint:</strong> {systemConfig.bkashSandbox 
                      ? 'https://tokenized.sandbox.bka.sh/v1.2.0-beta' 
                      : 'https://tokenized.pay.bka.sh/v1.2.0-beta'}
                  </Typography>
                </Alert>

                {systemConfig.bkashEnabled && (
                  <>
                    <TextField
                      fullWidth
                      label="bKash App Key"
                      value={systemConfig.bkashAppKey}
                      onChange={(e) => setSystemConfig(prev => ({ ...prev, bkashAppKey: e.target.value }))}
                      placeholder="Your bKash App Key"
                      type="password"
                      helperText="bKash merchant app key from developer portal"
                    />

                    <TextField
                      fullWidth
                      label="bKash App Secret"
                      value={systemConfig.bkashAppSecret}
                      onChange={(e) => setSystemConfig(prev => ({ ...prev, bkashAppSecret: e.target.value }))}
                      placeholder="Your bKash App Secret"
                      type="password"
                      helperText="bKash merchant app secret"
                    />

                    <Box display="flex" gap={2}>
                      <TextField
                        fullWidth
                        label="bKash Username"
                        value={systemConfig.bkashUsername}
                        onChange={(e) => setSystemConfig(prev => ({ ...prev, bkashUsername: e.target.value }))}
                        placeholder="Your bKash merchant username"
                      />
                      <TextField
                        fullWidth
                        label="bKash Password"
                        value={systemConfig.bkashPassword}
                        onChange={(e) => setSystemConfig(prev => ({ ...prev, bkashPassword: e.target.value }))}
                        placeholder="Your bKash merchant password"
                        type="password"
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" gutterBottom>
                      bKash Notification Variables:
                    </Typography>
                    <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Use these variables in SMS/Email templates for bKash payments:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {[
                          { variable: 'bkashTrxId', desc: 'bKash Transaction ID' },
                          { variable: 'bkashPaymentId', desc: 'bKash Payment ID' },
                          { variable: 'paymentAmount', desc: 'Payment Amount (৳)' },
                          { variable: 'paymentStatus', desc: 'Payment Status' },
                          { variable: 'paymentMethod', desc: 'Payment Method (bkash)' }
                        ].map(({ variable, desc }) => (
                          <Chip 
                            key={variable}
                            label={`{{${variable}}}`}
                            size="small" 
                            variant="outlined"
                            color="primary"
                            title={desc}
                          />
                        ))}
                      </Box>

                      <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                        <strong>Example Templates:</strong>
                      </Typography>
                      <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, border: '1px solid #ddd' }}>
                        <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', mb: 1 }}>
                          "Payment successful! Amount: ৳{'{{paymentAmount}}'} | bKash TrxID: {'{{bkashTrxId}}'} | Ref: {'{{uniqueId}}'}"
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                          "Hi {'{{firstName}}'}, your payment via bKash is confirmed. Transaction: {'{{bkashTrxId}}'}"
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    bKash Features:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="✅ Tokenized payment API integration"
                        secondary="Secure payment processing with bKash business API"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="✅ Automatic transaction tracking"
                        secondary="Real-time payment status updates and notifications"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="✅ Sandbox and production modes"
                        secondary="Test payments safely before going live"
                      />
                    </ListItem>
                  </List>
                </Box>

                {/* Save Button for bKash Settings */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={saveSystemSettings}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save bKash Settings'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Bank Transfer Settings Tab */}
        <TabPanel value={activeTab} index={3}>
          <PaymentSettingsManager />
        </TabPanel>

        {/* Logs Tab */}
        <TabPanel value={activeTab} index={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <HistoryIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6">System Activity Logs</Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  System logs are available in the server console. Check your backend logs for detailed activity information.
                </Typography>
              </Alert>

              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  How to Monitor System Activity:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="📊 Server Logs"
                      secondary="Check backend console for real-time notification and payment status"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="💰 Payment Tracking"
                      secondary="bKash transactions and bank transfer verifications are logged"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="📱 Notification Status"
                      secondary="SMS and email delivery status with detailed error messages"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="🧪 Test Functions"
                      secondary="Use test features to verify all system components"
                    />
                  </ListItem>
                </List>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>

      {/* Test Notification Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Test Notifications</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Alert severity="info">
              Test the notification system by sending a sample SMS or email with payment variables.
            </Alert>

            <TextField
              fullWidth
              label="Test Email Address"
              type="email"
              value={testData.email}
              onChange={(e) => setTestData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="test@example.com"
              helperText="Leave empty to skip email test"
            />

            <TextField
              fullWidth
              label="Test Phone Number"
              value={testData.phone}
              onChange={(e) => setTestData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="01714694499"
              helperText="Bangladesh phone number format"
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Test Message"
              value={testData.message}
              onChange={(e) => setTestData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Payment of ৳{{paymentAmount}} received via {{paymentMethod}}. bKash TrxID: {{bkashTrxId}}"
              helperText="You can use payment variables like {{bkashTrxId}}, {{paymentAmount}}, etc."
            />

            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="caption">
                Test messages will use sample data for payment variables (e.g., bkashTrxId: TEST-123).
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleTestNotification} 
            variant="contained" 
            disabled={testing}
            startIcon={testing ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {testing ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
